import produce from 'immer';

import {StepItemStatus} from 'ui-library/StepNav';

import {
  ImportInstrumentDataModalState,
  InstrumentData,
  InstrumentQueryStatus,
  StepKey,
} from './InstrumentDataConstants';

export enum ImportInstrumentDataModalActionType {
  IS_IMPORTING = 'IS_IMPORTING',
  IS_LOGGING_IN = 'IS_LOGGING_IN',
  IS_RETRIEVING = 'IS_RETRIEVING',
  LOGIN = 'LOGIN',
  SELECT_ROW = 'SELECT_ROW',
  SET_INSTRUMENT_DATA = 'SET_INSTRUMENT_DATA',
  SET_PROCESS_INSTRUMENT_DATA = 'SET_PROCESS_INSTRUMENT_DATA',
  SET_INSTRUMENT_QUERY_FILTER = 'SET_INSTRUMENT_QUERY_FILTER',
  UPDATE_CURRENT_STEP = 'UPDATE_CURRENT_STEP',
}

interface IsImportingAction {
  type: ImportInstrumentDataModalActionType.IS_IMPORTING;
  isImporting: boolean;
}

interface IsLoggingInAction {
  type: ImportInstrumentDataModalActionType.IS_LOGGING_IN;
  isLoggingIn: boolean;
}

interface LoginAction {
  type: ImportInstrumentDataModalActionType.LOGIN;
  loginQueryStatus: InstrumentQueryStatus;
  loginUsername: string;
}

interface SelectRowAction {
  type: ImportInstrumentDataModalActionType.SELECT_ROW;
  selectedRowIndex: number;
}

interface SetInstrumentData {
  type: ImportInstrumentDataModalActionType.SET_INSTRUMENT_DATA;
  instrumentData: Array<InstrumentData>;
}

interface SetProcessInstrumentData {
  type: ImportInstrumentDataModalActionType.SET_PROCESS_INSTRUMENT_DATA;
  instrumentData: Array<InstrumentData>;
}

interface SetInstrumentQueryFilter {
  type: ImportInstrumentDataModalActionType.SET_INSTRUMENT_QUERY_FILTER;
  instrumentQueryIds: Array<string>;
}

interface UpdateCurrentStepAction {
  type: ImportInstrumentDataModalActionType.UPDATE_CURRENT_STEP;
  nextStepKey: StepKey;
}

export type ImportInstrumentDataModalAction =
  | IsImportingAction
  | IsLoggingInAction
  | LoginAction
  | SelectRowAction
  | SetInstrumentData
  | SetProcessInstrumentData
  | SetInstrumentQueryFilter
  | UpdateCurrentStepAction;

export default function importInstrumentDataModalReducer(
  state: ImportInstrumentDataModalState,
  action: ImportInstrumentDataModalAction
): ImportInstrumentDataModalState {
  switch (action.type) {
    case ImportInstrumentDataModalActionType.IS_IMPORTING:
      return produce(state, (draftState) => {
        draftState.isImporting = action.isImporting;
      });
    case ImportInstrumentDataModalActionType.IS_LOGGING_IN:
      return produce(state, (draftState) => {
        draftState.isLoggingIn = action.isLoggingIn;
        draftState.loginQueryStatus = InstrumentQueryStatus.PENDING;
      });
    case ImportInstrumentDataModalActionType.LOGIN:
      return produce(state, (draftState) => {
        draftState.isLoggingIn = false;
        draftState.loginQueryStatus = action.loginQueryStatus;
        if (action.loginQueryStatus === InstrumentQueryStatus.DONE) {
          draftState.loginUsername = action.loginUsername;
        }
      });
    case ImportInstrumentDataModalActionType.SELECT_ROW: {
      const newSelectedRowIndices = new Set(state.selectedRowIndices);
      if (newSelectedRowIndices.has(action.selectedRowIndex)) {
        newSelectedRowIndices.delete(action.selectedRowIndex);
      } else {
        newSelectedRowIndices.add(action.selectedRowIndex);
      }
      return produce(state, (draftState) => {
        draftState.selectedRowIndices = newSelectedRowIndices;
      });
    }
    case ImportInstrumentDataModalActionType.SET_INSTRUMENT_DATA:
      return produce(state, (draftState) => {
        draftState.instrumentData = action.instrumentData;
      });
    case ImportInstrumentDataModalActionType.SET_PROCESS_INSTRUMENT_DATA:
      return produce(state, (draftState) => {
        draftState.selectedProcessInstrumentData = action.instrumentData;
      });
    case ImportInstrumentDataModalActionType.SET_INSTRUMENT_QUERY_FILTER:
      return produce(state, (draftState) => {
        draftState.instrumentQueryFilter = action.instrumentQueryIds;
      });
    case ImportInstrumentDataModalActionType.UPDATE_CURRENT_STEP: {
      return produce(state, (draftState) => {
        draftState.currentStepKey = action.nextStepKey;
        draftState.statusByStep[state.currentStepKey] = StepItemStatus.DONE;
        draftState.statusByStep[action.nextStepKey] = StepItemStatus.UNVISITED;
        draftState.selectedRowIndices = new Set<number>();
        draftState.isImporting = false;
        draftState.instrumentData = [];
      });
    }
    default:
      return state;
  }
}
